import React, { createContext, useContext, useEffect, useState } from 'react';
import { API_V1, fetchApi, getImageUrl } from '../config/api';
import { refAmount } from '../utils/money';

const CartContext = createContext();
const CART_STORAGE_KEY = 'chefmargaro_cart_items_v1';

const normalizeQuantity = (value) => {
  const qty = Number(value);
  if (!Number.isFinite(qty) || qty <= 0) return 1;
  return Math.floor(qty);
};

const normalizeItemFromStorage = (item) => ({
  ...item,
  quantity: normalizeQuantity(item.quantity),
  // El precio SIEMPRE debe venir de API al rehidratar.
  price: '0.00',
});

const serializeCartForStorage = (items) =>
  items.map(({ price, ...rest }) => rest);

const extractNumericId = (itemId, prefix) => {
  if (typeof itemId !== 'string') return null;
  const regex = new RegExp(`^${prefix}-(\\d+)$`);
  const match = itemId.match(regex);
  return match ? Number(match[1]) : null;
};

const pickPayload = (data) => data?.data ?? data;

const fetchItemSnapshotFromApi = async (item) => {
  const fallback = { ...item, price: '0.00' };

  try {
    // Tipos principales de carrito
    if (item.type === 'product') {
      const productId = extractNumericId(item.id, 'product');
      if (!productId) return fallback;
      const res = await fetchApi(`${API_V1}/products/${productId}`);
      if (!res.ok) return fallback;
      const payload = pickPayload(await res.json());
      return {
        ...fallback,
        name: payload?.name ?? fallback.name,
        image: getImageUrl(payload?.image ?? payload?.thumbnail ?? payload?.thumbnail_url) || fallback.image,
        price: refAmount(payload?.price_eur ?? 0),
      };
    }

    if (item.type === 'combo') {
      const comboId = item.comboId ?? extractNumericId(item.id, 'combo');
      if (!comboId) return fallback;
      const res = await fetchApi(`${API_V1}/combos/${comboId}`);
      if (!res.ok) return fallback;
      const payload = pickPayload(await res.json());
      return {
        ...fallback,
        name: payload?.name ?? fallback.name,
        image: getImageUrl(payload?.image ?? payload?.thumbnail ?? payload?.thumbnail_url) || fallback.image,
        price: refAmount(payload?.price_eur ?? fallback.price ?? 0),
      };
    }

    // Extras/salsas del carrito (segun backend puede venir como /extras/{id})
    if (item.type === 'extra' || item.type === 'sauce') {
      const extraId = item.extraId ?? (Number.isFinite(Number(item.id)) ? Number(item.id) : null);
      if (!extraId) return fallback;
      const res = await fetchApi(`${API_V1}/extras/${extraId}`);
      if (!res.ok) return fallback;
      const payload = pickPayload(await res.json());
      return {
        ...fallback,
        name: payload?.title ?? payload?.name ?? fallback.name,
        image: getImageUrl(payload?.image_url ?? payload?.image ?? payload?.thumbnail ?? payload?.thumbnail_url) || fallback.image,
        price: refAmount(payload?.price_eur ?? 0),
      };
    }
  } catch {
    return fallback;
  }

  return fallback;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(normalizeItemFromStorage);
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const refreshPricesFromApi = async () => {
      if (!cartItems.length) return;
      const refreshed = await Promise.all(cartItems.map(fetchItemSnapshotFromApi));
      if (isCancelled) return;
      setCartItems((prev) => {
        if (!prev.length) return prev;
        return prev.map((prevItem, index) => ({
          ...prevItem,
          ...refreshed[index],
          quantity: prevItem.quantity,
        }));
      });
    };

    refreshPricesFromApi();
    return () => {
      isCancelled = true;
    };
    // Solo al montar: rehidratar precios desde API
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      const payload = serializeCartForStorage(cartItems);
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // ignore storage errors
    }
  }, [cartItems]);

  const addToCart = (item) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(i => i.id === item.id && i.type === item.type);
      if (existingItem) {
        return prevItems.map(i =>
          i.id === item.id && i.type === item.type
            ? {
                ...i,
                quantity: i.quantity + item.quantity,
                ...(item.combinaciones && { combinaciones: item.combinaciones }),
                ...(item.comboId != null && { comboId: item.comboId }),
                ...(item.extraId != null && { extraId: item.extraId }),
                ...(item.rollsConSesamo !== undefined && {
                  rollsConSesamo: item.rollsConSesamo,
                }),
                ...(item.rollsConQuesoCremaCebollin !== undefined && {
                  rollsConQuesoCremaCebollin: item.rollsConQuesoCremaCebollin,
                }),
              }
            : i
        );
      }
      return [...prevItems, item];
    });
  };

  const removeFromCart = (itemId, itemType) => {
    setCartItems(prevItems => prevItems.filter(i => !(i.id === itemId && i.type === itemType)));
  };

  const updateQuantity = (itemId, itemType, quantity) => {
    if (quantity <= 0) {
      removeFromCart(itemId, itemType);
      return;
    }
    setCartItems(prevItems =>
      prevItems.map(i =>
        i.id === itemId && i.type === itemType
          ? { ...i, quantity }
          : i
      )
    );
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => {
      return total + (parseFloat(item.price) * item.quantity);
    }, 0).toFixed(2);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const toggleCart = () => {
    setIsCartOpen(prev => !prev);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };

  const clearCart = () => {
    setCartItems([]);
    setIsCartOpen(false);
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        getTotalPrice,
        getTotalItems,
        isCartOpen,
        toggleCart,
        closeCart,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

